<template>
  <v-layout>
    <v-dialog v-bind:value="dialog" @click:outside="$emit('updateDialog', false)" max-width="860px">
      <template v-slot:activator="{ on }">
        <v-btn color="primary" class="mb-5" dark v-on="on" @click="$emit('updateDialog', true)">{{ button_name }}</v-btn>
      </template>

      <v-card>
        <v-form v-on:submit.prevent="createItem">
          <v-card-title class="pb-2">
            <span class="headline">{{ modal_title }}</span>
          </v-card-title>

          <v-card-text>

            <v-alert
              v-if="isSystemAccount && !isViewModalComputed"
              dense
              text
              type="info"
              class="mb-4"
            >
              Cuenta del sistema: solo podés editar la cuenta contable.
            </v-alert>

            <!-- 1. TIPO DE CUENTA -->
            <div class="caption font-weight-bold text-uppercase grey--text text--darken-1 mb-3">
              1. Tipo de Cuenta
            </div>
            <div class="d-flex flex-wrap mb-1">
              <div
                v-for="type in sortedAccountTypes"
                :key="type.id"
                class="account-type-card mr-3 mb-3"
                :class="{
                  'account-type-card--selected': isSelectedType(type),
                  'account-type-card--disabled': !isSelectedType(type) && !isTypeSelectable(type),
                  'account-type-card--readonly': isSelectedType(type) && !isTypeSelectable(type),
                }"
                @click="isTypeSelectable(type) && selectAccountType(type)"
              >
                <v-icon size="26" :color="getCardIconColor(type)">
                  {{ getTypeIcon(type) }}
                </v-icon>
                <div class="caption mt-1 font-weight-medium text-center">{{ type.name }}</div>
              </div>
            </div>
            <div v-if="showAccountTypeError" class="red--text caption mb-2">El tipo de cuenta es requerido</div>
            <div v-if="typeChangeNotice" class="grey--text caption mb-2">{{ typeChangeNotice }}</div>
            <v-alert v-if="showBankDataWarning" dense text type="warning" class="mb-2">
              Al guardar se van a eliminar el CBU y el alias de la cuenta.
            </v-alert>

            <v-divider class="mb-4 mt-1"></v-divider>

            <!-- 2. DATOS GENERALES -->
            <div class="caption font-weight-bold text-uppercase grey--text text--darken-1 mb-3">
              2. Datos Generales
            </div>
            <div class="row">
              <div class="col-md-3">
                <v-text-field
                  data-vv-as="Código"
                  label="Código*"
                  name="code"
                  v-model="item.code"
                  v-validate="codeRules"
                  maxlength="4"
                  hint="Hasta 4 caracteres, sin espacios ni guiones"
                  persistent-hint
                  :disabled="disableGeneral"
                  @keydown="onCodeKeydown"
                ></v-text-field>
                <span class="red--text">{{ errors.first("code") }}</span>
              </div>
              <div class="col-md-5">
                <v-text-field
                  data-vv-as="Nombre"
                  label="Nombre*"
                  name="name"
                  v-model="item.name"
                  v-validate="'required'"
                  :disabled="disableGeneral"
                ></v-text-field>
                <span class="red--text">{{ errors.first("name") }}</span>
              </div>
              <div class="col-md-4">
                <v-combobox
                  v-model="item.emitter"
                  data-vv-as="emisor"
                  name="emitter"
                  item-text="name"
                  item-value="id"
                  :items="emitters"
                  label="Empresa*"
                  autocomplete="off"
                  v-validate="'required|combobox_required'"
                  :loading="loading_emitters"
                  :disabled="disableGeneral"
                  @change="onEmitterChange"
                ></v-combobox>
                <span class="red--text">{{ errors.first("emitter") }}</span>
              </div>
            </div>

            <!-- 3. DATOS ESPECÍFICOS (condicional según tipo) -->
            <template v-if="hasSpecificFields">
              <v-divider class="mb-4 mt-1"></v-divider>
              <div class="caption font-weight-bold text-uppercase grey--text text--darken-1 mb-3">
                3. Datos Específicos
              </div>
              <div class="row">
                <div class="col-md-6" v-if="isBanco">
                  <v-combobox
                    v-model="item.sub_account_type"
                    data-vv-as="Sub tipo"
                    name="sub_account_type"
                    item-text="name"
                    item-value="id"
                    :items="sub_account_types"
                    label="Sub tipo*"
                    autocomplete="off"
                    v-validate="'required|combobox_required'"
                    :loading="loading_sub_account_types"
                    :disabled="disableGeneral"
                  ></v-combobox>
                  <span class="red--text">{{ errors.first("sub_account_type") }}</span>
                </div>

                <div class="col-md-6" v-if="isBanco">
                  <v-combobox
                    v-model="item.bank"
                    data-vv-as="banco"
                    name="bank"
                    item-text="name"
                    item-value="id"
                    :items="banks"
                    label="Banco"
                    autocomplete="off"
                    :loading="loading_banks"
                    v-validate="'combobox'"
                    :disabled="disableGeneral"
                  ></v-combobox>
                  <span class="red--text">{{ errors.first("bank") }}</span>
                </div>

                <div class="col-md-6" v-if="isBanco">
                  <v-text-field
                    data-vv-as="Alias"
                    label="Alias"
                    name="alias"
                    v-model="item.alias"
                    :disabled="disableGeneral"
                  ></v-text-field>
                </div>

                <div class="col-md-6" v-if="isBanco">
                  <v-text-field
                    data-vv-as="CBU"
                    label="CBU"
                    name="cbu"
                    v-model="item.cbu"
                    maxlength="22"
                    :disabled="disableGeneral"
                  ></v-text-field>
                </div>

                <!-- Formato del extracto bancario para la conciliación -->
                <div class="col-md-6" v-if="isBanco">
                  <v-select
                    v-model="item.statement_parser"
                    :items="statement_parsers"
                    item-text="name"
                    item-value="id"
                    label="Formato de extracto (conciliación)"
                    hint="Vacío = se usa el formato del banco si está soportado (Galicia, Provincia)"
                    persistent-hint
                    clearable
                    :disabled="isViewModal()"
                    @change="onStatementParserChange"
                  ></v-select>
                </div>

                <template v-if="isBanco && item.statement_parser === 'GENERICO' && item.statement_mapping">
                  <div class="col-12 pb-0">
                    <div class="caption grey--text text--darken-1">
                      Indicá la letra de la columna del Excel (A, B, C...) donde está cada dato. Usá "Importe" si el banco
                      informa un único importe con signo, o "Débito" y "Crédito" si vienen en columnas separadas.
                    </div>
                  </div>
                  <div class="col-md-2 col-4" v-for="field in mapping_fields" :key="field.key">
                    <v-text-field
                      v-model="item.statement_mapping[field.key]"
                      :label="field.label"
                      :maxlength="field.key === 'header_row' || field.key === 'date_format' ? 10 : 3"
                      dense
                      :disabled="isViewModal()"
                    ></v-text-field>
                  </div>
                </template>

                <div class="col-md-8" v-if="isCheque">
                  <v-combobox
                    v-model="item.parent_money_account"
                    data-vv-as="Cuenta Corriente"
                    name="parent_money_account"
                    item-text="name"
                    item-value="id"
                    :items="parent_money_accounts"
                    label="Cuenta Corriente asociada"
                    hint="Cuenta de banco tipo cuenta corriente asociada a la chequera"
                    autocomplete="off"
                    v-validate="'combobox'"
                    :loading="loading_parent_money_accounts"
                    :disabled="disableGeneral || !item.emitter"
                  ></v-combobox>
                  <span class="red--text">{{ errors.first("parent_money_account") }}</span>
                </div>
              </div>
            </template>

            <!-- CONTABILIDAD (cuenta del Plan de Cuentas) -->
            <v-divider class="mb-4 mt-1"></v-divider>
            <div class="caption font-weight-bold text-uppercase grey--text text--darken-1 mb-3">
              Cuenta Contable
            </div>
            <div class="row">
              <div class="col-md-8">
                <v-combobox
                  v-model="item.account"
                  data-vv-as="cuenta contable"
                  name="account"
                  :item-text="accountLabel"
                  item-value="id"
                  :items="accounts"
                  :label="accountRequired ? 'Cuenta contable*' : 'Cuenta contable'"
                  autocomplete="off"
                  :loading="loading_accounts"
                  v-validate="accountRequired ? 'required|combobox_required' : 'combobox'"
                  :disabled="isViewModal() || !item.emitter"
                  hint="Cuenta del Plan de Cuentas del emisor, usada por el motor contable"
                  persistent-hint
                ></v-combobox>
                <span class="red--text">{{ errors.first("account") }}</span>
              </div>
            </div>

            <!-- 4. CONFIGURACIÓN -->
            <v-divider class="mb-3 mt-2"></v-divider>
            <div class="caption font-weight-bold text-uppercase grey--text text--darken-1 mb-1">
              {{ hasSpecificFields ? "4." : "3." }} Configuración
            </div>
            <div class="d-flex align-center flex-wrap">
              <v-checkbox
                v-if="showRepresentaFondos"
                label="Representa Fondos"
                :disabled="disableGeneral"
                v-model="item.representa_fondos"
                class="mr-6"
              ></v-checkbox>
              <v-checkbox
                v-if="isOtras"
                label="Cuenta de Resultado"
                hint="Marcar si esta cuenta representa un ingreso u egreso (resultado), y no un fondo o medio de pago."
                persistent-hint
                :disabled="disableGeneral"
                v-model="item.is_resultado"
                class="mr-6"
              ></v-checkbox>
              <v-checkbox
                label="Habilitado"
                :disabled="disableGeneral"
                v-model="item.enabled"
              ></v-checkbox>
            </div>

          </v-card-text>

          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="error" text @click="$emit('updateDialog', false)">Cerrar</v-btn>
            <v-btn color="success" text type="submit" v-if="!isViewModal()" :loading="loading">{{ formBtn }}</v-btn>
          </v-card-actions>
        </v-form>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import Constant from "@/constants/sections";
import ModalGenericMixin from "@/mixins/ModalGenericMixin.vue";
import { emmiterService } from "@/libs/ws/emmiterService";
import { generalService } from "@/libs/ws/generalService";

export default {
  mixins: [ModalGenericMixin],
  props: {
    parent: Object,
    service: Object,
    modal_title: String,
    dialog: Boolean,
    item: Object,
    button_name: String,
    formBtn: String,
  },
  data() {
    return {
      show: false,
      triedSubmit: false,
      emitters: [],
      loading_emitters: false,
      banks: [],
      loading_banks: false,
      account_types: [],
      loading_account_types: false,
      sub_account_types: [],
      loading_sub_account_types: false,
      statement_parsers: [],
      mapping_fields: [
        { key: "header_row", label: "Fila encabezado" },
        { key: "date_col", label: "Fecha*" },
        { key: "date_format", label: "Formato fecha" },
        { key: "concept_col", label: "Descripción*" },
        { key: "reference_col", label: "Referencia" },
        { key: "amount_col", label: "Importe" },
        { key: "debit_col", label: "Débito" },
        { key: "credit_col", label: "Crédito" },
        { key: "balance_col", label: "Saldo" },
      ],
      parent_money_accounts: [],
      loading_parent_money_accounts: false,
      accounts: [],
      loading_accounts: false,
      original: null,
    };
  },
  computed: {
    isSystemAccount() {
      return !!(this.item && this.item.internal);
    },
    disableGeneral() {
      return this.isViewModalComputed || this.isSystemAccount;
    },
    codeRules() {
      return this.item && this.item.id ? "required|regex:^\\S+$" : "required|max:4|regex:^[^\\s-]+$";
    },
    hasMovements() {
      return !!(this.item && this.item.id) && this.item.has_movements !== false;
    },
    hasChequesOrChildren() {
      return !!(this.item && this.item.id) && (this.item.has_cheques !== false || this.item.has_children !== false);
    },
    originalTypeId() {
      if (this.original) return this.original.type;
      return this.currentTypeId;
    },
    currentTypeId() {
      const t = this.item && this.item.account_type;
      return t && typeof t === "object" ? t.id : t;
    },
    typeChangeNotice() {
      if (!this.item || !this.item.id || this.isViewModalComputed || this.isSystemAccount || !this.hasMovements) return "";
      if (this.hasChequesOrChildren)
        return "La cuenta tiene cheques o chequeras asociados: el tipo de cuenta no se puede modificar.";
      return ["B", "E", "T"].includes(this.originalTypeId)
        ? "La cuenta tiene movimientos: solo se puede cambiar entre Banco, Efectivo y Tarjeta."
        : "La cuenta tiene movimientos: el tipo de cuenta no se puede modificar.";
    },
    showBankDataWarning() {
      return !!(
        this.item && this.item.id && this.original && this.original.type === "B" &&
        (this.original.cbu || this.original.alias) && this.currentTypeId && this.currentTypeId !== "B"
      );
    },
    isViewModalComputed() {
      return this.formBtn == "";
    },
    accountRequired() {
      const em = this.item.emitter;
      return !!(em && typeof em === "object" && em.contabilidad_habilitada);
    },
    sortedAccountTypes() {
      const priority = (type) => {
        const name = type.name.toLowerCase();
        if (name.includes("banco")) return 0;
        if (name.includes("efectivo") || name.includes("caja")) return 1;
        return 2;
      };
      return [...this.account_types].sort((a, b) => priority(a) - priority(b));
    },
    isBanco() {
      if (!this.item.account_type) return false;
      const id = typeof this.item.account_type === "object" ? this.item.account_type.id : this.item.account_type;
      return id === "B";
    },
    isCheque() {
      if (!this.item.account_type) return false;
      const id = typeof this.item.account_type === "object" ? this.item.account_type.id : this.item.account_type;
      return id === "C";
    },
    isEfectivo() {
      if (!this.item.account_type) return false;
      const name = typeof this.item.account_type === "object" ? this.item.account_type.name.toLowerCase() : "";
      return name.includes("efectivo") || name.includes("caja");
    },
    isOtras() {
      if (!this.item.account_type) return false;
      const id = typeof this.item.account_type === "object" ? this.item.account_type.id : this.item.account_type;
      return id === "O";
    },
    showRepresentaFondos() {
      return !this.isBanco && !this.isEfectivo;
    },
    hasSpecificFields() {
      return this.isBanco || this.isCheque;
    },
    showAccountTypeError() {
      return this.triedSubmit && !this.item.account_type;
    },
    subAccountTypeId() {
      if (!this.item.sub_account_type) return null;
      return typeof this.item.sub_account_type === "object"
        ? this.item.sub_account_type.id
        : this.item.sub_account_type;
    },
    showSubAccountTypeError() {
      return this.isBanco && this.triedSubmit && !this.subAccountTypeId;
    },
  },
  watch: {
    item() {
      this.captureOriginal();
      this.$emit("updateItem", this.item);
      if (this.item && this.item.emitter) {
        const emitterId = typeof this.item.emitter === "object" ? this.item.emitter.id : this.item.emitter;
        if (emitterId) {
          this.loadParentMoneyAccountsByEmitter(emitterId);
          this.loadAccountsByEmitter(emitterId);
        }
      } else {
        this.parent_money_accounts = [];
        this.accounts = [];
      }
    },
    dialog() {
      this.errors.clear();
      if (!this.dialog) {
        this.triedSubmit = false;
        this.parent_money_accounts = [];
        this.accounts = [];
      } else {
        this.captureOriginal();
      }
      if (this.dialog && this.emitters.length === 1 && !this.item.emitter) {
        this.item.emitter = this.emitters[0];
        this.onEmitterChange(this.emitters[0]);
        this.$emit("updateItem", this.item);
      }
    },
  },
  mounted() {
    this.loadComboData();
  },
  methods: {
    captureOriginal() {
      const item = this.item;
      this.original = item && item.id
        ? {
            type: this.currentTypeId,
            sub_account_type: item.sub_account_type,
            cbu: item.cbu,
            alias: item.alias,
            parent_money_account: item.parent_money_account,
            is_resultado: item.is_resultado,
          }
        : null;
    },
    isTypeSelectable(type) {
      if (this.isViewModalComputed || this.isSystemAccount) return false;
      if (!this.item.id) return true;
      if (this.hasChequesOrChildren) return false;
      if (!this.hasMovements) return true;
      const fondos = ["B", "E", "T"];
      return fondos.includes(this.originalTypeId) && fondos.includes(type.id);
    },
    getCardIconColor(type) {
      if (this.isSelectedType(type)) return this.getTypeIconColor(type);
      return this.isTypeSelectable(type) ? "grey darken-1" : "grey lighten-1";
    },
    onCodeKeydown(e) {
      if (e.key === " " || (e.key === "-" && !this.item.id)) e.preventDefault();
    },
    isSelectedType(type) {
      if (!this.item.account_type) return false;
      const id = typeof this.item.account_type === "object" ? this.item.account_type.id : this.item.account_type;
      return id === type.id;
    },
    selectAccountType(type) {
      this.item.account_type = type;
      this.onAccountTypeChange();
      this.$emit("updateItem", this.item);
    },
    getTypeIcon(type) {
      if (!type) return "account_balance_wallet";
      const name = type.name.toLowerCase();
      if (name.includes("banco")) return "account_balance";
      if (name.includes("cheque")) return "receipt_long";
      if (name.includes("tarjeta")) return "credit_card";
      if (name.includes("efectivo") || name.includes("caja")) return "payments";
      return "more_horiz";
    },
    getTypeIconColor(type) {
      if (!type) return "#757575";
      const name = type.name.toLowerCase();
      if (name.includes("banco")) return "#1565C0";
      if (name.includes("cheque")) return "#455A64";
      if (name.includes("tarjeta")) return "#7B1FA2";
      if (name.includes("efectivo") || name.includes("caja")) return "#2E7D32";
      return "#757575";
    },
    loadComboData() {
      this.loading_emitters = true;
      emmiterService
        .filter()
        .then((response) => { this.emitters = response.result; })
        .finally(() => { this.loading_emitters = false; });

      this.loading_banks = true;
      generalService.setSection(Constant.SECTION_BANKS);
      generalService
        .filter({ has_bank: true })
        .then((response) => { this.banks = response.result; })
        .catch((e) => { console.log("banks.filter error", e); })
        .finally(() => { this.loading_banks = false; });

      this.loading_account_types = true;
      generalService.setSection(Constant.SECTION_MONEY_ACCOUNTS);
      generalService
        .getCustom("account_types")
        .then((response) => { this.account_types = response.result; })
        .finally(() => { this.loading_account_types = false; });

      this.loading_sub_account_types = true;
      generalService.setSection(Constant.SECTION_MONEY_ACCOUNTS);
      generalService
        .getCustom("sub_account_types")
        .then((response) => { this.sub_account_types = response.result; })
        .finally(() => { this.loading_sub_account_types = false; });

      generalService.setSection(Constant.SECTION_MONEY_ACCOUNTS);
      generalService
        .getCustom("statement_parsers")
        .then((response) => { this.statement_parsers = response.result; })
        .catch(() => {});
    },
    onStatementParserChange(val) {
      if (val === "GENERICO" && !this.item.statement_mapping) {
        this.$set(this.item, "statement_mapping", { header_row: "1", date_col: "", date_format: "d/m/Y", concept_col: "", reference_col: "", amount_col: "", debit_col: "", credit_col: "", balance_col: "" });
      }
    },
    onEmitterChange(val) {
      this.parent_money_accounts = [];
      this.item.parent_money_account = null;
      this.accounts = [];
      this.item.account = null;
      if (!val) return;
      const emitterId = typeof val === "object" ? val.id : val;
      if (emitterId) {
        this.loadParentMoneyAccountsByEmitter(emitterId);
        this.loadAccountsByEmitter(emitterId);
      }
    },
    accountLabel(account) {
      if (!account) return "";
      return account.code ? account.code + " - " + account.name : account.name;
    },
    loadAccountsByEmitter(emitterId) {
      this.loading_accounts = true;
      generalService.setSection(Constant.SECTION_ACCOUNTS);
      generalService
        .filter({ emitter: emitterId, imputable: true })
        .then((response) => { this.accounts = response.result || []; })
        .catch((e) => { console.log("accounts.filter error", e); })
        .finally(() => { this.loading_accounts = false; });
    },
    loadParentMoneyAccountsByEmitter(emitterId) {
      this.loading_parent_money_accounts = true;
      generalService.setSection(Constant.SECTION_MONEY_ACCOUNTS);
      generalService
        .filter({ account_type: "B", emitter_id: emitterId })
        .then((response) => {
          this.parent_money_accounts = (response.result || []).filter((moneyAccount) => {
            const subType =
              typeof moneyAccount.sub_account_type === "object"
                ? moneyAccount.sub_account_type.id
                : moneyAccount.sub_account_type;
            return subType === "CC";
          });
        })
        .catch((e) => { console.log("parent_money_accounts.filter error", e); })
        .finally(() => { this.loading_parent_money_accounts = false; });
    },
    onAccountTypeChange() {
      const id = typeof this.item.account_type === "object" ? this.item.account_type.id : this.item.account_type;
      const name = typeof this.item.account_type === "object" ? this.item.account_type.name.toLowerCase() : "";

      if (id !== "B") {
        this.item.sub_account_type = null;
        this.item.alias = null;
        this.item.cbu = null;
      }
      if (id !== "C") {
        this.item.parent_money_account = null;
      }
      if (id !== "O") {
        this.item.is_resultado = false;
      }
      if (id === "B" || name.includes("efectivo") || name.includes("caja")) {
        this.item.representa_fondos = true;
      }
      if (this.item.id && this.original && id === this.original.type) {
        this.item.sub_account_type = this.original.sub_account_type;
        this.item.cbu = this.original.cbu;
        this.item.alias = this.original.alias;
        this.item.parent_money_account = this.original.parent_money_account;
        this.item.is_resultado = this.original.is_resultado;
      }
    },
    isViewModal() {
      return this.formBtn == "";
    },
    createItem(e) {
      this.triedSubmit = true;
      if (!this.item.account_type) return;

      generalService.setSection(Constant.SECTION_MONEY_ACCOUNTS);
      this.$validator.validate().then((valid) => {
        if (valid) {
          e.preventDefault();
          this.loading = true;

          if (this.isBanco && !this.subAccountTypeId) {
            this.$toast.error("El campo Sub tipo es obligatorio", "Error");
            this.loading = false;
            return;
          }

          var data = {
            id:                   this.item.id,
            code:                 this.item.code,
            name:                 this.item.name,
            account_type:         this.item.account_type,
            sub_account_type:     this.isBanco ? this.item.sub_account_type : null,
            parent_money_account: this.isCheque ? this.item.parent_money_account : null,
            representa_fondos:    this.item.representa_fondos,
            is_resultado:         this.isOtras ? this.item.is_resultado : false,
            alias:                this.isBanco ? this.item.alias : null,
            cbu:                  this.isBanco ? this.item.cbu : null,
            statement_parser:     this.isBanco ? this.item.statement_parser || null : null,
            statement_mapping:    this.isBanco && this.item.statement_parser === "GENERICO" ? this.item.statement_mapping : null,
            emitter:              this.item.emitter,
            bank:                 this.item.bank,
            enabled:              this.item.enabled,
            account:              this.item.account,
          };

          if (this.item.ctacte == null) data["ctacte"] = 0;

          if (this.item.id > 0) {
            this.service
              .update(data)
              .then((response) => {
                if (response.status == true) {
                  this.$toast.success("El " + this.modal_title + " se modifico correctamente.", "OK");
                  this.$emit("updateDialog", false);
                  this.$emit("loadList");
                } else {
                  this.$toast.error(response.message || response.msg, "Error");
                }
              })
              .catch((response) => { this.$toast.error(response.msg, "Error"); })
              .finally(() => (this.loading = false));
          } else {
            this.service
              .create(data)
              .then((response) => {
                if (response.status) {
                  this.$toast.success("El " + this.modal_title + " se creo correctamente", "OK");
                  this.$emit("updateDialog", false);
                  this.$emit("loadList");
                } else {
                  this.$toast.error(response.message || response.msg, "Error");
                }
              })
              .catch((response) => { this.$toast.error(response.msg, "Error"); })
              .finally(() => (this.loading = false));
          }
        }
      });
    },
  },
};
</script>

<style scoped>
.account-type-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 14px 20px;
  min-width: 90px;
  border: 1.5px solid #E0E0E0;
  border-radius: 10px;
  cursor: pointer;
  transition: border-color 0.2s ease, background 0.2s ease;
}

.account-type-card:hover:not(.account-type-card--disabled):not(.account-type-card--readonly) {
  border-color: #1565C0;
  background: #F5F9FF;
}

.account-type-card--selected {
  border-color: #1565C0 !important;
  background: #E3F2FD !important;
}

.account-type-card--readonly {
  cursor: default;
}

.account-type-card--disabled {
  cursor: not-allowed;
  opacity: 0.4;
}
</style>
